import { CheckCircle, XCircle, Star } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Review } from '../types/review';
import { ReviewStatusBadge } from './ReviewStatusBadge';
import { formatDate } from '../utils/formatDate';

interface ReviewDetailPanelProps {
  readonly review: Review;
  readonly apartmentName: string;
  readonly isApproving: boolean;
  readonly isRejecting: boolean;
  readonly onApprove: (id: string) => void;
  readonly onReject: (id: string) => void;
  readonly onClose: () => void;
}

/** Renders a filled star rating (0–5). */
const StarRating = ({ rating }: { rating: number }) => {
  const { t } = useTranslation('pages');
  const full = Math.floor(rating);
  return (
    <span className="flex items-center gap-0.5" aria-label={t('starRating', { count: rating })}>
      {Array.from({ length: 5 }, (_, i) => (
        <Star
          key={i}
          size={14}
          className={i < full ? 'text-amber-400 fill-amber-400' : 'text-gray-300 fill-gray-300'}
        />
      ))}
      <span className="ml-1 text-sm text-gray-600">{rating}</span>
    </span>
  );
};

export const ReviewDetailPanel = ({
  review,
  apartmentName,
  isApproving,
  isRejecting,
  onApprove,
  onReject,
  onClose,
}: ReviewDetailPanelProps) => {
  const { t } = useTranslation(['common', 'pages', 'errors', 'forms', 'badges', 'navigation']);
  const canAct = review.status === 'pending' && !isApproving && !isRejecting;

  return (
    <aside
      className="fixed inset-y-0 right-0 w-96 bg-white border-l border-gray-200 shadow-xl flex flex-col z-20"
      aria-label={t('pages:reviewDetailsAriaLabel')}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
        <h2 className="text-lg font-semibold text-gray-900">{t('pages:reviewDetailsHeading')}</h2>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 transition-colors"
          aria-label={t('pages:closePanel')}
        >
          <XCircle size={20} />
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
        {/* Meta */}
        <div className="space-y-2 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-gray-500">{t('pages:fieldApartment')}</span>
            <span className="font-medium text-gray-900">{apartmentName}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-500">{t('pages:fieldCustomer')}</span>
            <span className="font-medium text-gray-900">{review.customerName}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-500">{t('pages:fieldRating')}</span>
            <StarRating rating={review.rating} />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-500">{t('pages:fieldStatus')}</span>
            <ReviewStatusBadge status={review.status} />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-500">{t('pages:fieldSource')}</span>
            <span className="text-gray-700 capitalize">{review.source}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-500">{t('pages:columnSubmitted')}</span>
            <span className="text-gray-700">{formatDate(review.createdAt.slice(0, 10))}</span>
          </div>
        </div>

        {/* Review text */}
        <div>
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
            {t('pages:sectionReview')}
          </p>
          <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">{review.text}</p>
        </div>
      </div>

      {/* Actions */}
      {review.status === 'pending' && (
        <div className="px-6 py-4 border-t border-gray-100 flex gap-3">
          <button
            onClick={() => onApprove(review.id)}
            disabled={!canAct}
            className="flex-1 flex items-center justify-center gap-2 bg-green-600 text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <CheckCircle size={15} />
            {isApproving ? t('pages:approving') : t('pages:approve')}
          </button>
          <button
            onClick={() => onReject(review.id)}
            disabled={!canAct}
            className="flex-1 flex items-center justify-center gap-2 bg-red-600 text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <XCircle size={15} />
            {isRejecting ? t('pages:rejecting') : t('pages:reject')}
          </button>
        </div>
      )}
    </aside>
  );
};
